import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import type {
  Filter,
  FilterDefinition,
} from "@/shared/components/ResourceView";
import {
  CreateSessionButton,
  SessionsList,
  useCoreSessionFilters,
  useSessionCrossIdFilters,
} from "@/shared/components/sessions";

import { useDatasetContext } from "../useDatasetContext";

import styles from "./DatasetSessions.module.css";

/**
 * "Sessions" tab on the dataset-detail route. Renders the shared
 * `<SessionsList>` scoped to the current dataset, plus a "New session"
 * toolbar action that opens the create-session page prefilled with this
 * dataset. The scope is enforced server-side via the `dataset.dataset_id`
 * cross-aggregate filter and cannot be widened by the user.
 *
 * Mounted only when `featureFlagRoute` in `DatasetsRoutes` admits
 * `/datasets/:datasetPath/sessions` under the `release-sessions` flag; with
 * the flag off the route redirects to the dataset's Files tab.
 */
export function DatasetSessions() {
  const { datasetId } = useDatasetContext();
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const coreFilters = useCoreSessionFilters(resolvedOrgId);
  const crossIdFilters = useSessionCrossIdFilters(resolvedOrgId);

  const scope = React.useMemo<Filter[]>(
    () => [
      {
        type: "string",
        field: "dataset.dataset_id",
        comparator: "EQUALS",
        values: [datasetId],
      },
    ],
    [datasetId],
  );

  // Omits `dataset.dataset_id` (pinned as the server-side scope) and adds
  // `device.device_id` as the remaining cross-aggregate narrowing axis.
  const filterDefinitions = React.useMemo<FilterDefinition[]>(
    () => [...coreFilters, crossIdFilters.deviceIdFilter],
    [coreFilters, crossIdFilters],
  );

  return (
    <SessionsList
      className={styles.sessionList}
      scope={scope}
      filterDefinitions={filterDefinitions}
      noRowsContent="No sessions in this dataset"
      actions={<CreateSessionButton datasetId={datasetId} />}
    />
  );
}
